import React from "react";
import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

// EU AI Act transparency surface. Field-driven from the serializer's ai_label
// ('no_ai' | 'partially_ai' | 'fully_ai'). Renders NOTHING for no_ai/missing - the
// mark is the exception, not the rule. Two distinct states on purpose (assisted vs
// generated) so the honesty of the three-state model is not collapsed. Warn-only:
// this never gates anything. Quiet neutral styling (not accent), so it does not fight
// the verified check or the artist chip on the same card. Dark-mode via CSS vars.
// English defaultValue only until the nine locales land at aiLabel.*.
const EN = {
  "badge.partial": "Partly AI",
  "badge.full": "AI-generated",
  "detail.partial": "Some elements of this content were created using AI.",
  "detail.full": "This content was created using AI.",
};

const stateKey = (label) => (label === "partially_ai" ? "partial" : label === "fully_ai" ? "full" : null);

// Small pill for content cards. aria-label is the full honest sentence (the detail
// copy), so a screen reader never hits a bare icon.
export const AiLabelBadge = ({ label, className = "", testid = "ai-label-badge" }) => {
  const { t } = useTranslation();
  const k = stateKey(label);
  if (!k) return null;
  const detail = t(`aiLabel.detail.${k}`, { defaultValue: EN[`detail.${k}`] });
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full ${className}`}
      style={{ fontSize: 10, fontWeight: 600, lineHeight: 1.2, padding: "2px 7px", background: "var(--bg-subtle)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" }}
      data-testid={`${testid}-${k}`}
      role="note"
      aria-label={detail}
      title={detail}
    >
      <Sparkles size={11} aria-hidden="true" style={{ flexShrink: 0 }} />
      {t(`aiLabel.badge.${k}`, { defaultValue: EN[`badge.${k}`] })}
    </span>
  );
};

// One quiet line for detail views.
export const AiLabelLine = ({ label, className = "", testid = "ai-label-line" }) => {
  const { t } = useTranslation();
  const k = stateKey(label);
  if (!k) return null;
  return (
    <p
      className={`flex items-center gap-1.5 text-xs ${className}`}
      style={{ color: "var(--text-secondary)" }}
      data-testid={`${testid}-${k}`}
    >
      <Sparkles size={13} aria-hidden="true" style={{ flexShrink: 0 }} />
      {t(`aiLabel.detail.${k}`, { defaultValue: EN[`detail.${k}`] })}
    </p>
  );
};

export default AiLabelBadge;
